import { IBackend } from '@/api/BackendApi';
import { SampleActivityDto, ActivityCategoryDto } from '@/api/ApiClient';
import { TreeNode } from '@/types/tree';

import {
  createRecipeActivitySubmitValidation,
  createStorageActivitySubmitValidation,
  createProjectActivitySubmitValidation,
  createTheoryActivitySubmitValidation,
  createDataPackageSubmitValidation,
  createShippingSubmitValidation,
  toDataPackageSubmitValidationInput,
} from '@/lib/validation/submitValidationBuilders';
import { toRecipeTemplateStep } from '@/lib/utils/recipeTemplateStep';
import { validateRequiredToSubmit, ValidationResult } from '@/lib/utils/submitValidation';

export interface SubmitValidationOutcome {
  isValid: boolean;
  errors: string[];
  fieldErrors: Record<string, string>;
  entityLabel: string;
}

/**
 * Builds submit validation from the same validation builders used by detail footers.
 * This keeps context-menu submit aligned with footer submit rules.
 */
export async function resolveSubmitValidationForNode(
  node: TreeNode,
  backend: IBackend,
  getTypedData: (node: TreeNode) => any
): Promise<SubmitValidationOutcome> {
  let validationResult: ValidationResult = { isValid: true, errors: [], fieldErrors: {} };
  let entityLabel = 'Entity';

  switch (node.type) {
    case 'sampleActivity': {
      entityLabel = 'Sample Activity';
      const activity = getTypedData(node) as SampleActivityDto | null;
      if (!activity) {
        return {
          isValid: false,
          errors: ['Sample Activity data not found'],
          fieldErrors: {},
          entityLabel,
        };
      }

      if (activity.category === ActivityCategoryDto.Storage) {
        validationResult = validateRequiredToSubmit(createStorageActivitySubmitValidation(activity));
        break;
      }

      let templateUsesInstrument = false;
      if (activity.recipeTemplate && activity.type) {
        const step = toRecipeTemplateStep(activity.type);
        if (step) {
          try {
            const template = await backend.recipeTemplate.get(step, null, null, activity.recipeTemplate);
            templateUsesInstrument = template?.usesInstrument === true;
          } catch {
            // Fallback keeps prior behavior for non-template-dependent validation.
            templateUsesInstrument = false;
          }
        }
      }

      validationResult = validateRequiredToSubmit(
        createRecipeActivitySubmitValidation(activity, {
          templateUsesInstrument,
        })
      );
      break;
    }
    case 'projectActivity': {
      entityLabel = 'Project Activity';
      const activity = getTypedData(node);
      validationResult = validateRequiredToSubmit(
        createProjectActivitySubmitValidation({ ...activity, files: activity?.files || [] })
      );
      break;
    }
    case 'theoryActivity': {
      entityLabel = 'Theory Activity';
      const activity = getTypedData(node);
      validationResult = validateRequiredToSubmit(createTheoryActivitySubmitValidation(activity || {}));
      break;
    }
    case 'dataPackage': {
      entityLabel = 'Data Package';
      const dataPackage = getTypedData(node);
      validationResult = validateRequiredToSubmit(
        createDataPackageSubmitValidation(toDataPackageSubmitValidationInput(dataPackage || {}))
      );
      break;
    }
    case 'shipping': {
      entityLabel = 'Shipping';
      const shipping = getTypedData(node);
      validationResult = validateRequiredToSubmit(createShippingSubmitValidation(shipping || {}));
      break;
    }
    default:
      // For node types without explicit footer submit validation, keep existing behavior.
      break;
  }

  return {
    isValid: validationResult.isValid,
    errors: validationResult.errors,
    fieldErrors: validationResult.fieldErrors,
    entityLabel,
  };
}
